import React, { useEffect, useCallback } from 'react';
import { useTabs } from '../../context/TabsContext';
import './NavigationButtons.css';

/**
 * Back/Forward navigation buttons for in-app tab navigation
 * 
 * Each tab has its own navigation history (like browser tabs).
 * These buttons navigate within the active tab's history.
 */
const NavigationButtons = ({ onNavigate }) => {
  const { canGoBack, canGoForward, goBack, goForward } = useTabs();
  
  const canBack = canGoBack();
  const canForward = canGoForward();

  const handleBack = useCallback(() => {
    const result = goBack();
    if (result && onNavigate) {
      // Pass both the entry we're navigating to and the previous state
      onNavigate(result.entry, result.previousState);
    }
  }, [goBack, onNavigate]);

  const handleForward = useCallback(() => {
    const result = goForward();
    if (result && onNavigate) {
      // Pass both the entry we're navigating to and the previous state
      onNavigate(result.entry, result.previousState);
    }
  }, [goForward, onNavigate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt+Left for back, Alt+Right for forward
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        if (e.key === 'ArrowLeft' && canBack) {
          e.preventDefault();
          handleBack();
        } else if (e.key === 'ArrowRight' && canForward) {
          e.preventDefault();
          handleForward();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack, handleForward, canBack, canForward]);

  return (
    <div className="nav-buttons">
      <button
        className="nav-buttons__btn"
        onClick={handleBack}
        disabled={!canBack}
        title="Go back (Alt+←)"
        aria-label="Go back"
      >
        <svg className="nav-buttons__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        className="nav-buttons__btn"
        onClick={handleForward}
        disabled={!canForward}
        title="Go forward (Alt+→)"
        aria-label="Go forward"
      >
        <svg className="nav-buttons__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
};

export default NavigationButtons;
